// Displays ATS resume analysis results — score, keyword gaps, section feedback.

import { useState } from "react";
import {
  ChevronDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Copy,
  Minus,
} from "lucide-react";
import { toast } from "sonner";

interface KeywordGap {
  keyword: string;
  importance: "high" | "medium" | "low";
  context: string;
}

interface SectionFeedback {
  name: string;
  score: number;
  feedback: string;
  suggestion: string;
}

export interface AtsResult {
  score: number;
  verdict: string;
  summary: string;
  keywordGaps: KeywordGap[];
  sections: SectionFeedback[];
}

interface Props {
  result: AtsResult;
}

const IMPORTANCE_STYLES: Record<string, string> = {
  high: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800",
  medium:
    "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800",
  low: "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800",
};

function ScoreGauge({ score }: { score: number }) {
  let color = "text-red-500";
  let bgColor = "bg-red-100 dark:bg-red-900/30";
  let ringColor = "stroke-red-500";

  if (score >= 90) {
    color = "text-emerald-600";
    bgColor = "bg-emerald-100 dark:bg-emerald-900/30";
    ringColor = "stroke-emerald-500";
  } else if (score >= 70) {
    color = "text-green-600";
    bgColor = "bg-green-100 dark:bg-green-900/30";
    ringColor = "stroke-green-500";
  } else if (score >= 50) {
    color = "text-amber-600";
    bgColor = "bg-amber-100 dark:bg-amber-900/30";
    ringColor = "stroke-amber-500";
  }

  const circumference = 2 * Math.PI * 54;
  const offset = circumference - (score / 100) * circumference;

  return (
    <div className={`relative w-32 h-32 ${bgColor} rounded-2xl flex items-center justify-center`}>
      <svg className="absolute w-28 h-28 -rotate-90" viewBox="0 0 120 120">
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          stroke="currentColor"
          className="text-border"
          strokeWidth="6"
        />
        <circle
          cx="60"
          cy="60"
          r="54"
          fill="none"
          className={ringColor}
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 1s ease-out" }}
        />
      </svg>
      <span className={`text-3xl font-bold ${color} z-10`}>{score}</span>
    </div>
  );
}

function SectionBar({ score }: { score: number }) {
  let color = "bg-red-400";
  if (score >= 90) color = "bg-emerald-500";
  else if (score >= 70) color = "bg-green-500";
  else if (score >= 50) color = "bg-amber-400";

  return (
    <div className="h-1.5 w-full bg-border rounded-full overflow-hidden">
      <div
        className={`h-full rounded-full ${color}`}
        style={{ width: `${score}%`, transition: "width 0.8s ease-out" }}
      />
    </div>
  );
}

export default function AtsScoreCard({ result }: Props) {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  const score = Number.isFinite(result.score) ? Math.min(100, Math.max(0, result.score)) : 0;
  const keywordGaps = result.keywordGaps ?? [];
  const sections = result.sections ?? [];

  const VerdictIcon =
    score >= 70 ? CheckCircle : score >= 50 ? Minus : XCircle;

  const verdictColor =
    score >= 70
      ? "text-green-600 dark:text-green-400"
      : score >= 50
        ? "text-amber-600 dark:text-amber-400"
        : "text-red-600 dark:text-red-400";

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="rounded-xl border border-border bg-card p-5 flex items-center gap-6">
        <ScoreGauge score={score} />
        <div className="flex-1">
          <div className={`flex items-center gap-2 ${verdictColor}`}>
            <VerdictIcon className="w-5 h-5" />
            <span className="font-display font-bold text-lg">
              {result.verdict || "Analysis"}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            {result.summary || ""}
          </p>
        </div>
      </div>

      {keywordGaps.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            <h4 className="font-display font-semibold text-sm text-foreground">
              Missing Keywords
            </h4>
            <span className="text-xs text-muted-foreground">
              ({keywordGaps.length} gaps found)
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            {keywordGaps.map((gap, i) => (
              <div
                key={`${gap.keyword}-${i}`}
                className={`group relative px-2.5 py-1 rounded-lg border text-xs font-medium cursor-default ${IMPORTANCE_STYLES[gap.importance] ?? IMPORTANCE_STYLES.low}`}
              >
                {gap.keyword}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg bg-foreground text-background text-xs max-w-[200px] text-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-20">
                  {gap.context}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-foreground" />
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-3 text-[10px] text-muted-foreground pt-1">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-red-400" /> High priority
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-400" /> Medium
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-400" /> Nice to have
            </span>
          </div>
        </div>
      )}

      {sections.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-display font-semibold text-sm text-foreground">
            Section Breakdown
          </h4>

          {sections.map((section, idx) => {
            const sectionKey = `${section.name}-${idx}`;
            const isExpanded = expandedSection === sectionKey;
            const secScore = Number.isFinite(section.score)
              ? Math.min(100, Math.max(0, section.score))
              : 0;
            return (
              <div
                key={sectionKey}
                className="rounded-xl border border-border bg-card overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() =>
                    setExpandedSection(isExpanded ? null : sectionKey)
                  }
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex-1 text-left">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-foreground">
                        {section.name}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {secScore}/100
                      </span>
                    </div>
                    <SectionBar score={secScore} />
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform duration-200 ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                        Analysis
                      </p>
                      <p className="text-sm text-foreground leading-relaxed">
                        {section.feedback}
                      </p>
                    </div>

                    {section.suggestion && (
                      <div className="rounded-lg bg-primary/5 border border-primary/10 p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <p className="text-xs font-medium text-primary">
                            Suggested improvement
                          </p>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(
                                section.suggestion
                              );
                              toast.success("Copied suggestion!");
                            }}
                            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                          >
                            <Copy className="w-3 h-3" />
                            Copy
                          </button>
                        </div>
                        <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                          {section.suggestion}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
